import { Avatar, Popover } from '@affine/component';
import { PublicUserLabel } from '@affine/core/modules/cloud/views/public-user';
import {
  carriesOwnName,
  DocumentPresenceAvatarStack,
} from '@affine/core/modules/organize';
import { useI18n } from '@affine/i18n';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AnimatedRegion,
  type AnimatedRegionProps,
} from '../animation/animated-region';
import * as styles from './participant-overlay.css';
import { ParticipantPanel } from './participant-panel';

/**
 * The popover's content from its opening until it has collapsed. It arrives collapsed, so the
 * opening is a reveal like every later one.
 */
function ParticipantsRegion({
  open,
  onCollapsed,
  ...props
}: Omit<AnimatedRegionProps, 'expanded' | 'onSettled'> & {
  open: boolean;
  onCollapsed: () => void;
}) {
  const [arrived, setArrived] = useState(false);
  useLayoutEffect(() => setArrived(true), []);
  const expanded = open && arrived;
  const revealed = useRef(false);
  if (expanded) revealed.current = true;
  // Closed before it was ever revealed: nothing is left to collapse.
  useLayoutEffect(() => {
    if (!open && !revealed.current) onCollapsed();
  }, [open, onCollapsed]);
  return (
    <AnimatedRegion
      {...props}
      expanded={expanded}
      onSettled={settled => {
        if (!settled) onCollapsed();
      }}
    />
  );
}

export function DocumentParticipantsMenu({
  userIds,
  userNames,
  scopedIdentities = false,
  hoverPreview = false,
  avatarSize = 28,
  onSelectUser,
  triggerLabel,
  triggerClassName,
}: {
  userIds: readonly string[] | undefined;
  userNames?: ReadonlyMap<string, string>;
  scopedIdentities?: boolean;
  hoverPreview?: boolean;
  avatarSize?: number;
  onSelectUser?: (userId: string) => void;
  triggerLabel?: string;
  triggerClassName?: string;
}) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  // Mounted only from its opening until it has collapsed: a closed popover that stays mounted
  // keeps its dismiss layer and takes every Escape of the app.
  const [mounted, setMounted] = useState(false);
  if (open && !mounted) setMounted(true);
  const present = open || mounted;
  const unmount = useCallback(() => setMounted(false), []);
  const [search, setSearch] = useState('');
  const [pinned, setPinnedState] = useState(false);
  const pinnedRef = useRef(false);
  const setPinned = (value: boolean) => {
    pinnedRef.current = value;
    setPinnedState(value);
  };
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const enter = () => {
    cancelClose();
    if (hoverPreview) setOpen(true);
  };
  const leave = () => {
    if (!hoverPreview || pinnedRef.current) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const ids = useMemo(() => [...new Set(userIds ?? [])], [userIds]);
  const filtered = ids.filter(
    id =>
      !search.trim() ||
      userNames
        ?.get(id)
        ?.toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase())
  );
  if (ids.length === 0) return null;
  return (
    <Popover
      open={open}
      onOpenChange={value => {
        setOpen(value);
        if (!value) {
          setSearch('');
          setPinned(false);
          cancelClose();
        }
      }}
      portalOptions={{ forceMount: present || undefined }}
      contentOptions={{
        forceMount: present || undefined,
        align: 'end',
        className: styles.documentParticipantsPopover,
        onOpenAutoFocus: event => {
          if (hoverPreview && !pinned) event.preventDefault();
        },
      }}
      content={
        <ParticipantsRegion
          open={open}
          onCollapsed={unmount}
          animateContent
          returnFocusRef={triggerRef}
          className={styles.documentParticipantsSurface}
          onPointerEnter={enter}
          onPointerLeave={leave}
          onClick={event => event.stopPropagation()}
        >
          <ParticipantPanel
            searchRef={searchRef}
            search={search}
            onSearchChange={setSearch}
            placeholder={t[
              'com.streamlain.collaboration.participants.search'
            ]()}
          >
            {filtered.map(id => (
              <button
                key={id}
                type="button"
                className={styles.participantRow}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelectUser?.(id);
                  if (onSelectUser) {
                    setOpen(false);
                    setPinned(false);
                  }
                }}
              >
                {scopedIdentities || carriesOwnName(id) ? (
                  <span className={styles.scopedParticipantIdentity}>
                    <Avatar
                      name={userNames?.get(id)}
                      size={20}
                      avatarTooltipOptions={{ content: null }}
                    />
                    <span>{userNames?.get(id)}</span>
                  </span>
                ) : (
                  <PublicUserLabel
                    id={id}
                    size={20}
                    align="center"
                    showTooltip={false}
                  />
                )}
              </button>
            ))}
            {filtered.length === 0 ? (
              <div className={styles.emptyState}>
                {t['com.streamlain.collaboration.participants.empty']()}
              </div>
            ) : null}
          </ParticipantPanel>
        </ParticipantsRegion>
      }
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel ?? t['Members']()}
        className={triggerClassName ?? styles.documentParticipantsTrigger}
        onPointerEnter={enter}
        onPointerLeave={leave}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          cancelClose();
          if (pinnedRef.current) {
            setOpen(false);
            setPinned(false);
            setSearch('');
          } else {
            setOpen(true);
            setPinned(true);
            requestAnimationFrame(() => searchRef.current?.focus());
          }
        }}
      >
        {triggerLabel ?? (
          <DocumentPresenceAvatarStack
            userIds={ids}
            userNames={userNames}
            scopedIdentities={scopedIdentities}
            size={avatarSize}
            showTooltip={false}
            maxVisible={4}
          />
        )}
      </button>
    </Popover>
  );
}
